import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { TuiAutoFocusModule } from '@taiga-ui/cdk';
import { TuiButtonModule, TuiDialogContext, TuiSvgModule } from '@taiga-ui/core';
import { TuiInputModule } from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { AuthActions, selectSessionUser } from '@us/web/auth/data-access';
import { CartActions, CartMember } from '@us/web/cart/data-access';
import { MessageService } from '@us/web/shared/feature';

@Component({
    selector: 'us-join-order',
    standalone: true,
    imports: [
        IonicModule,
        TranslocoModule,
        NgOptimizedImage,
        TuiButtonModule,
        TuiSvgModule,
        ReactiveFormsModule,
        TuiInputModule,
        TuiAutoFocusModule
    ],
    templateUrl: './join-order.component.html',
    styleUrls: ['./join-order.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class JoinOrderComponent {
    private readonly context = inject(POLYMORPHEUS_CONTEXT) as TuiDialogContext<
        void,
        {
            cartId: string;
        }
    >;
    private readonly fb = inject(FormBuilder);
    private readonly store = inject(Store);
    private readonly messageService = inject(MessageService);
    private readonly translocoService = inject(TranslocoService);
    private readonly router = inject(Router);
    private readonly cdk = inject(ChangeDetectorRef);

    readonly form: FormGroup;

    readonly loading = signal(false);

    constructor() {
        const user = this.store.selectSignal(selectSessionUser)();
        this.form = this.fb.group({
            id: [user.id, [Validators.required]],
            name: [user.name, [Validators.required]]
        });
    }

    onSubmit() {
        if (this.form.invalid) {
            return;
        }
        this.loading.set(true);

        const user = this.form.getRawValue() as CartMember;

        this.store.dispatch(AuthActions.updateSessionUserName({ name: user.name }));

        this.store.dispatch(
            CartActions.joinSharedOrder({
                cartId: this.context.data.cartId,
                user,
                onSuccess: async () => {
                    this.loading.set(false);
                    this.messageService.success(this.translocoService.translate('success'));
                    await this.router.navigate([]);
                    this.context.completeWith();
                    this.cdk.detectChanges();
                },
                onError: async error => {
                    this.loading.set(false);
                    this.messageService.error(error);
                    this.cdk.detectChanges();
                }
            })
        );
    }
}
